import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, comicImages, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);
  const comic = comics.find((c) => c.id === comicId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-lg">漫画未找到</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-3 inline-block whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-14 bg-background-50">
        <article className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <span className="text-foreground-300">/</span>
            <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              {categoryInfo?.name || comic.category}
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700 font-medium">{comic.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full lg:w-[300px]">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 sticky top-24">
                <img
                  src={comicImages[comic.id]}
                  alt={comic.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-700">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500"></i>
                  </div>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="text-sm text-foreground-500">
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-eye-line"></i>
                  </div>
                  {(comic.views / 10000).toFixed(0)}万 次阅读
                </span>
                <span className="text-sm text-foreground-500">
                  共 {comic.chapters} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-5 mb-6">
                <h2 className="text-base font-heading text-foreground-900 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-circle-line"></i>
                  </div>
                  从第一话开始阅读
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-full font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bookmark-line"></i>
                  </div>
                  收藏漫画
                </button>
              </div>

              <p className="text-xs text-foreground-400 mt-3">
                最新更新：{comic.updateChapter} · 更新日期：{comic.updateDate}
              </p>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200/70">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-links-line text-lg text-primary-500"></i>
                </div>
                <h2 className="text-lg font-heading text-foreground-900">同类推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link key={rc.id} to={`/comic/${rc.id}`} className="group block cursor-pointer">
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={comicImages[rc.id]}
                        alt={rc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-50/90 backdrop-blur-sm px-1.5 py-0.5 rounded-full">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-xs text-accent-500"></i>
                        </div>
                        <span className="text-xs font-semibold text-foreground-900">{rc.rating}</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': `${comic.title} - 虫虫漫画网`,
          'description': comic.description,
          'author': { '@type': 'Person', 'name': comic.author },
          'datePublished': '2026-01-01',
          'dateModified': comic.updateDate,
          'url': `https://www.chongchongmanhua.com/comic/${comic.id}`,
          'breadcrumb': {
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.chongchongmanhua.com/' },
              { '@type': 'ListItem', 'position': 2, 'name': categoryInfo?.name || '分类', 'item': `https://www.chongchongmanhua.com/category/${comic.category}` },
              { '@type': 'ListItem', 'position': 3, 'name': comic.title, 'item': `https://www.chongchongmanhua.com/comic/${comic.id}` }
            ]
          }
        })
      }} />
    </>
  );
}